import Button from "@/components/Form/Button/Button";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { havingEditPermission } from "@/utility/common";
import DistributionProcessingPopup from "../processing/ProcessingPopup";
import DistributionContext from "./DistributionContext";
import {
  useGetDistributionSummaryByIdQuery,
  useReadDistributionProcessingQuery,
} from "@/services/distribution/distributionReducers";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";

function GenerateDraftCalculation({
  handleSummaryApiAfterSettlementDone,
  distributionName,
  isPending,
  setpsData,
  processingData,
  setNotification,
}: {
  handleSummaryApiAfterSettlementDone?: () => void;
  distributionName: string;
  isPending: boolean;
  setpsData: any[] | undefined;
  processingData: any;
  setNotification?: any;
}) {
  const [isEntitlementDisabled, setIsEntitlementDisabled] = useState(false);
  const [isSettlementDisabled, setIsSettlementDisabled] = useState(false);
  const [showEntitlementPopup, setShowEntitlementPopup] = useState(false);
  const [showSettlementPopup, setShowSettlementPopup] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState("");
  const [enableNextButton, setEnableNextButton] = useState(false);
  const permissions = useSelector((state: any) => state.permissions);
  const { Distribution } = permissions;
  const { distributionId } = useParams();
  const { t } = useTranslation();
  const {
    data: distributionDataProcessing,
    refetch: refetchDistributionProcessingData,
  } = useReadDistributionProcessingQuery(distributionId, {
    skip: !distributionId,
  });
  const distributionContextData: any = useContext<any>(DistributionContext);
  const { refetch } = useGetDistributionSummaryByIdQuery({
    id: distributionId,
  });
  let interval = useRef<any>(null);
  const handleProgress=useCallback((trigger?: boolean)=> {
    refetchDistributionProcessingData()
      .then((res: any) => {
        const processingStatus = res?.data?.processingStatus;
        if (processingStatus === 1 || processingStatus === 2) {
          setNotification({
            show: true,
            message: t(
              "pages.distributions.distribution.caluclationPendingMessage"
            ),
            supportingMessage:"",
            type: "info",
          });
        } else if (processingStatus === 3) {
          setNotification({
            show: true,
            message: t("pages.distributions.distribution.caluclationError", {
              errorMessage: res?.data?.processingStatusMessage,
            }),
            supportingMessage:"",
            type: "error",
          });
        } else {
          setNotification({
            show: trigger,
            message: t("pages.distributions.distribution.caluclationSuccessMessage",{distributionName:distributionName}),
            supportingMessage: "",
            type: "success",
          });
        }
      })
      .catch((err: any) => {
        setNotification({
          show: true,
          supportingMessage:"",
          message: t("global.globalNotifications.wentWrong"),
          type: "error",
        });
      });
  },[refetchDistributionProcessingData,setNotification,t,distributionName]);  
  useEffect(() => {
    if (
      distributionDataProcessing?.processingStatus === 1 ||
      distributionDataProcessing?.processingStatus === 2
    ) {
      interval.current = setInterval(() => {
        handleProgress(true);
      }, 10000);
    }
    return () => clearInterval(interval.current);
  }, [distributionDataProcessing,handleProgress]);
  useEffect(()=>{
    handleProgress(false)

  },[handleProgress])

  return (
    <>
      {
        <div  className="button-container sticky-footer">
          {havingEditPermission(Distribution) && (
            <Button
              type="button"
              label={t(
                "pages.distributions.distribution.generateDraftSettlementButton"
              )}
              variant="primary"
              className="refreshancelBtn mar-b-5"
              onClickHandler={() => {
                if (![1, 2]?.includes(processingData.processingStatus)) {
                  setShowConfirmationMessage(
                    t(
                      "pages.distributions.distribution.generateDraftSettlementConfirmationMessage"
                    )
                  );
                } else {
                  setEnableNextButton(true);
                  setShowConfirmationMessage(
                    t(
                      "pages.distributions.distribution.caluclationConfirmationMessage"
                    )
                  );
                }
                setShowConfirmationModal(true);
                setIsSettlementDisabled(true);
              }}
              disabled={
                !processingData ||
                isSettlementDisabled ||
                distributionContextData.distributionStatus === 3 ||
                !setpsData ||
                setpsData?.length === 0
              }
            />
          )}
          {havingEditPermission(Distribution) && (
            <Button
              type="button"
              label={t(
                "pages.distributions.distribution.generateDraftEntitlementButton"
              )}
              variant="primary"
              className="refreshancelBtn"
              onClickHandler={() => {
                if (![1, 2]?.includes(processingData.processingStatus)) {
                  setShowConfirmationMessage(
                    t(
                      "pages.distributions.distribution.generateDraftEntitlementConfirmationMessage"
                    )
                  );
                } else {
                  setEnableNextButton(true);
                  setShowConfirmationMessage(
                    t(
                      "pages.distributions.distribution.caluclationConfirmationMessage"
                    )
                  );
                }
                setShowConfirmationModal(true);

                setIsEntitlementDisabled(true);
              }}
              disabled={
                !processingData ||
                isEntitlementDisabled ||
                distributionContextData.distributionStatus === 3 ||
                !setpsData ||
                setpsData?.length === 0
              }
            />
          )}
        </div>
      }
      {showEntitlementPopup && (
        <DistributionProcessingPopup
          showModal={true}
          distributionId={distributionId}
          distributionName={distributionName}
          type="entitlement"
          onClose={() => {
            handleProgress(true);

            refetch();
            setShowEntitlementPopup(false);
            setIsEntitlementDisabled(false);
          }}
        />
      )}
      {showSettlementPopup && (
        <DistributionProcessingPopup
          showModal={true}
          distributionId={distributionId}
          distributionName={distributionName}
          type="settlement"
          onClose={() => {
            handleProgress(true);

            refetch();
            setShowSettlementPopup(false);
            setIsSettlementDisabled(false);
          }}
        />
      )}
      <ConfirmationModal
        isVisible={showConfirmationModal}
        setIsVisible={() => {}}
        cancelBtnApproveHandler={() => {
          if (enableNextButton) {
            if (isSettlementDisabled) {
              setShowConfirmationMessage(
                t(
                  "pages.distributions.distribution.generateDraftSettlementConfirmationMessage"
                )
              );
            }
            if (isEntitlementDisabled) {
              setShowConfirmationMessage(
                t(
                  "pages.distributions.distribution.generateDraftEntitlementConfirmationMessage"
                )
              );
            }

            setEnableNextButton(false);
          } else {
            setNotification({
              show: false,
              message: "",
              type: "error",
              supportingMessage:"",

            });
            if (isSettlementDisabled) {
              setShowSettlementPopup(true);
            }
            if (isEntitlementDisabled) {
              setShowEntitlementPopup(true);
            }
            setShowConfirmationModal(false);
          }
        }}
        cancelBtnRejectHandler={() => {
          if (isSettlementDisabled) {
            setIsSettlementDisabled(false);
          }
          if (isEntitlementDisabled) {
            setIsEntitlementDisabled(false);
          }
          setShowConfirmationModal(false);
        }}
        showHeader={false}
        type={enableNextButton ? 4 : 3}
        message={showConfirmationMessage}
      ></ConfirmationModal>
    </>
  );
}

export default GenerateDraftCalculation;
